import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Stack, Button } from '@mui/material';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ArticleIcon from '@mui/icons-material/Article';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ReplyDialog from './ReplyDialog'; 
import { api, getContentText, getAuthorName } from '../api';
import VisibilityIcon from '@mui/icons-material/Visibility'; 
import XIcon from '@mui/icons-material/X';


const TikTokIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-2.43.03-4.83-.95-6.46-2.9-1.6-1.92-2.3-4.25-2.08-6.6-.02-1.38.62-2.78 1.66-3.79 1.04-1.01 2.36-1.57 3.8-1.62.22-.01 1.09.01 1.32.02.01-1.42-.01-2.84.02-4.26-.03-1.51-.1-3.02.6-4.33.61-1.16 1.66-1.98 2.96-2.31.42-.1.82-.13 1.25-.15.01-.11.01-.21.01-.32Z"/>
    </svg>
);

function getPlatformIcon(platform) {
    switch (platform) {
        case 'twitter': return <XIcon fontSize="small" sx={{ color: 'grey.600'  }} />;
        case 'youtube': return <YouTubeIcon fontSize="small" sx={{ color: 'grey.600'  }} />;
        case 'facebook':
        case 'facebook-group':
        case 'facebook-page': return <FacebookIcon fontSize="small" sx={{ color: 'grey.600' }} />;
        case 'instagram': return <InstagramIcon fontSize="small" sx={{ color: 'grey.600'  }} />;
        case 'linkedin': return <LinkedInIcon fontSize="small" sx={{ color: 'grey.600'  }} />;
        case 'linkedin post': return <LinkedInIcon fontSize="small" sx={{ color: 'grey.600'  }} />;
        case 'linkedin comment': return <LinkedInIcon fontSize="small" sx={{ color: 'grey.600'  }} />;
         case 'tiktok': return <TikTokIcon />;
        case 'News': return <NewspaperIcon fontSize="small" sx={{ color: 'grey.600' }} />;
        case 'simulation': return <PlayCircleOutlineIcon fontSize="small" />;
        default: return <ArticleIcon fontSize="small" />;
    }
}

export const MentionCard = ({ mention }) => {
    const sentimentColors = {
        Positive: { text: '#166534', bg: '#D1FAE5' },
        Negative: { text: '#991B1B', bg: '#FEE2E2' },
        Neutral: { text: '#374151', bg: '#E5E7EB' },
        Unanalyzed: { text: '#92400E', bg: '#FEF3C7' },
    };
    const color = sentimentColors[mention.sentiment] || sentimentColors.Unanalyzed;
    
    const [replyDialogOpen, setReplyDialogOpen] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [isReplyLoading, setIsReplyLoading] = useState(false);

    const handleGenerateReply = async () => {
        setReplyContent('');
        setReplyDialogOpen(true);
        setIsReplyLoading(true);
        try {
            const response = await api.post('/utility/generate-reply', { 
                content: getContentText(mention.content), 
                sentiment: mention.sentiment 
            });
            setReplyContent(response.data.reply);
        } catch (error) {
            setReplyContent('Sorry, could not generate a reply.');
        } finally {
            setIsReplyLoading(false);
        }
    };
       const platformName = mention.platform === 'twitter' ? 'X' : mention.platform;
    
    return (
        <>
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {getPlatformIcon(mention.platform)}
                            <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                            {platformName}
                        </Typography>
                            <Typography variant="caption" sx={{ bgcolor: color.bg, color: color.text, px: 1, py: 0.5, borderRadius: '12px', fontWeight: 500 }}>{mention.sentiment}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" startIcon={<ChatBubbleOutlineIcon />} onClick={handleGenerateReply} sx={{ fontWeight: 'bold' }}>Generate Reply</Button>
                            <Button size="small" href={mention.postUrl} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 'bold' }}>View Post</Button>
                        </Stack>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 2, wordBreak: 'break-word' }}>{getContentText(mention.content)}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 1, borderTop: '1px solid #eee', color: 'text.disabled' }}>
                        <Typography variant="caption">By: {getAuthorName(mention.author)}</Typography>
                        <Typography variant="caption">{new Date(mention.publishedAt).toLocaleString()}</Typography>
                    </Box>
                      {/* --- NEW: Reach and Engagement Metrics --- */}
                <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', mt: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}><VisibilityIcon fontSize="inherit" /><Typography variant="caption">Reach: {mention.reach || 0}</Typography></Stack>
                    <Typography variant="caption">Likes: {mention.likes || 0}</Typography>
                    <Typography variant="caption">Comments: {mention.comments || 0}</Typography>
                    <Typography variant="caption">Shares: {mention.shares || 0}</Typography>
                </Box>
                </CardContent>
            </Card>
            <ReplyDialog 
                open={replyDialogOpen} 
                onClose={() => setReplyDialogOpen(false)} 
                content={replyContent} 
                isLoading={isReplyLoading} 
            />
        </>
    );
};

export const RawMentionCard = ({ item }) => {
    const sentimentColors = {
        Positive: { text: '#166534', bg: '#D1FAE5' },
        Negative: { text: '#991B1B', bg: '#FEE2E2' },
        Neutral: { text: '#374151', bg: '#E5E7EB' },
        Unanalyzed: { text: '#92400E', bg: '#FEF3C7' },
    };
    const color = sentimentColors[item.sentiment] || sentimentColors.Unanalyzed;
    const displayContent = item.message || item.text || '[No text content]';

    const [replyDialogOpen, setReplyDialogOpen] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [isReplyLoading, setIsReplyLoading] = useState(false);

    const handleGenerateReply = async () => {
        setReplyContent('');
        setReplyDialogOpen(true);
        setIsReplyLoading(true);
        try {
            const response = await api.post('/utility/generate-reply', { 
                content: displayContent, 
                sentiment: item.sentiment 
            });
            setReplyContent(response.data.reply);
        } catch (error) {
            setReplyContent('Sorry, could not generate a reply.');
        } finally {
            setIsReplyLoading(false);
        }
    };

    return (
        <>
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                     <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <FacebookIcon fontSize="small" sx={{ color: '#1877F2' }} />
                            <Typography variant="subtitle1">Facebook Post</Typography>
                            <Typography variant="caption" sx={{ bgcolor: color.bg, color: color.text, px: 1, py: 0.5, borderRadius: '12px', fontWeight: 'bold' }}>{item.sentiment}</Typography>
                            {item.gender === 'Male' && <MaleIcon color="primary" />}
                            {item.gender === 'Female' && <FemaleIcon color="secondary" />}
                        </Stack>
                         <Stack direction="row" spacing={1}>
                             <Button size="small" startIcon={<ChatBubbleOutlineIcon />} onClick={handleGenerateReply} sx={{ fontWeight: 'bold' }}>Generate Reply</Button>
                            <Button size="small" href={item.url} target="_blank" rel="noopener noreferrer"  sx={{ fontWeight: 'bold' }}>View Post</Button>
                        </Stack>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{displayContent}</Typography>
                    
                    {item.images && item.images.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 2 }}>
                            {item.images.map((img, index) => (
                                <img key={index} src={img} alt={`post content ${index + 1}`} style={{ height: '100px', borderRadius: '8px', objectFit: 'cover' }} />
                            ))}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, color: 'text.disabled', borderTop: '1px solid #eee', pt: 1 }}>
                        <Typography variant="caption">By: {getAuthorName(item.author) || item.author_name}</Typography>
                        <Typography variant="caption">{new Date(item.timestamp * 1000).toLocaleString()}</Typography>
                    </Box>
                     <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', mt: 1, color: 'text.secondary' }}>
                       <Typography variant="caption">Reach: {item.reach || item.reach || 0}</Typography>
                        <Typography variant="caption">Likes: {item.reactions_count || item.likes || 0}</Typography>
                        <Typography variant="caption">Comments: {item.comments_count || item.comments || 0}</Typography>
                        <Typography variant="caption">Shares: {item.reshare_count || item.shares || 0}</Typography>
                    </Box>
                </CardContent>
            </Card>
             <ReplyDialog 
                open={replyDialogOpen} 
                onClose={() => setReplyDialogOpen(false)} 
                content={replyContent} 
                isLoading={isReplyLoading} 
            />
        </>
    );
};

export const RawInstagramCard = ({ item }) => {
    const sentimentColors = {
        Positive: { text: '#166534', bg: '#D1FAE5' },
        Negative: { text: '#991B1B', bg: '#FEE2E2' },
        Neutral: { text: '#374151', bg: '#E5E7EB' },
        Unanalyzed: { text: '#92400E', bg: '#FEF3C7' },
    };
    const color = sentimentColors[item.sentiment] || sentimentColors.Unanalyzed;
    const displayContent = item.caption || '[No caption]';

    const [replyDialogOpen, setReplyDialogOpen] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [isReplyLoading, setIsReplyLoading] = useState(false);

    const handleGenerateReply = async () => {
        setReplyContent('');
        setReplyDialogOpen(true);
        setIsReplyLoading(true);
        try {
            const response = await api.post('/utility/generate-reply', { 
                content: displayContent, 
                sentiment: item.sentiment 
            });
            setReplyContent(response.data.reply);
        } catch (error) {
            setReplyContent('Sorry, could not generate a reply.');
        } finally {
            setIsReplyLoading(false);
        }
    };

    return (
        <>
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                     <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <InstagramIcon fontSize="small" sx={{ color: '#E4405F' }} />
                            <Typography variant="subtitle1">Instagram Post</Typography>
                            <Typography variant="caption" sx={{ bgcolor: color.bg, color: color.text, px: 1, py: 0.5, borderRadius: '12px', fontWeight: 'bold' }}>{item.sentiment}</Typography>
                        </Stack>
                         <Stack direction="row" spacing={1}>
                             <Button size="small" startIcon={<ChatBubbleOutlineIcon />} onClick={handleGenerateReply}  sx={{ fontWeight: 'bold' }}> Generate Reply</Button>
                            <Button size="small" href={item.url} target="_blank" rel="noopener noreferrer"  sx={{ fontWeight: 'bold' }}>View Post</Button>
                        </Stack>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{displayContent}</Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, color: 'text.disabled', borderTop: '1px solid #eee', pt: 1 }}>
                        <Typography variant="caption">By: {item.ownerUsername || 'Unknown'}</Typography>
                        <Typography variant="caption">{new Date(item.timestamp).toLocaleString()}</Typography>
                    </Box>
                     <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', mt: 1, color: 'text.secondary' }}>
                          <Typography variant="caption">Reach: {item.reach || item.reach || 0}</Typography>
                        <Typography variant="caption">Likes: {item.likesCount || 0}</Typography>
                        <Typography variant="caption">Comments: {item.commentsCount || 0}</Typography>
                    </Box>
                </CardContent>
            </Card>
             <ReplyDialog 
                open={replyDialogOpen} 
                onClose={() => setReplyDialogOpen(false)} 
                content={replyContent} 
                isLoading={isReplyLoading} 
            />
        </>
    );
};

export const RawTwitterCard = ({ item }) => {
    const [replyDialogOpen, setReplyDialogOpen] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [isReplyLoading, setIsReplyLoading] = useState(false);

    const handleGenerateReply = async () => {
        setReplyContent('');
        setReplyDialogOpen(true);
        setIsReplyLoading(true);
        try {
            const response = await api.post('/utility/generate-reply', { 
                content: item.text, 
                // Twitter raw data doesn't have pre-analyzed sentiment
                sentiment: 'Neutral' 
            });
            setReplyContent(response.data.reply);
        } catch (error) {
            setReplyContent('Sorry, could not generate a reply.');
        } finally {
            setIsReplyLoading(false);
        }
    };

    return (
        <>
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                             <XIcon fontSize="small" sx={{ color: '#1DA1F2' }} />
                            <Typography variant="subtitle1">Raw X Mention</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                             <Button size="small" startIcon={<ChatBubbleOutlineIcon />} onClick={handleGenerateReply} >Generate Reply</Button>
                            <Button size="small" href={`https://twitter.com/anyuser/status/${item.id}`} target="_blank" rel="noopener noreferrer">View Tweet</Button>
                        </Stack>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.text}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, color: 'text.disabled', borderTop: '1px solid #eee', pt: 1 }}>
                        <Typography variant="caption">By Author ID: {item.author_id}</Typography>
                        <Typography variant="caption">{new Date(item.created_at).toLocaleString()}</Typography>
                    </Box>
                     <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', mt: 1, color: 'text.secondary' }}>
                        <Typography variant="caption">Likes: {item.public_metrics?.like_count || 0}</Typography>
                        <Typography variant="caption">Replies: {item.public_metrics?.reply_count || 0}</Typography>
                        <Typography variant="caption">Retweets: {item.public_metrics?.retweet_count || 0}</Typography>
                         <Typography variant="caption">Quotes: {item.public_metrics?.quote_count || 0}</Typography>
                            <Typography variant="caption">Reach: {item.reach || item.reach || 0}</Typography>
                    </Box>
                </CardContent>
            </Card>
            <ReplyDialog 
                open={replyDialogOpen} 
                onClose={() => setReplyDialogOpen(false)} 
                content={replyContent} 
                isLoading={isReplyLoading} 
            />
        </>
    );
};



export const RawTiktokCard = ({ item, onGenerateReply }) => {
    const sentimentColors = {
        Positive: { text: '#166534', bg: '#D1FAE5' },
        Negative: { text: '#991B1B', bg: '#FEE2E2' },
        Neutral: { text: '#374151', bg: '#E5E7EB' },
        Unanalyzed: { text: '#92400E', bg: '#FEF3C7' },
    };
    const color = sentimentColors[item.sentiment] || sentimentColors.Unanalyzed;
    const displayContent = item.text || '[No description]';

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://www.tiktok.com/embed.js';
        script.async = true;
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
    }, []);

    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TikTokIcon />
                        <Typography variant="subtitle1">TikTok Post</Typography>
                        <Typography variant="caption" sx={{ bgcolor: color.bg, color: color.text, px: 1, py: 0.5, borderRadius: '12px', fontWeight: 500 }}>{item.sentiment}</Typography>
                    </Stack>
                     <Stack direction="row" spacing={1}>
                         <Button size="small" startIcon={<ChatBubbleOutlineIcon />} onClick={() => onGenerateReply({ content: displayContent, sentiment: item.sentiment })}>Reply</Button>
                        <Button size="small" href={item.webVideoUrl} target="_blank" rel="noopener noreferrer">View Post</Button>
                    </Stack>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{displayContent}</Typography>
                
                 {item.id && item.authorMeta?.name && (
                     <Box sx={{ my: 2, maxWidth: '325px', mx: 'auto' }}>
                        <blockquote className="tiktok-embed" cite={item.webVideoUrl} data-video-id={item.id} style={{ maxWidth: '605px', minWidth: '325px' }} > 
                            <section> 
                                <a target="_blank" rel="noopener noreferrer" title={`@${item.authorMeta.name}`} href={`https://www.tiktok.com/@${item.authorMeta.name}`}>@{item.authorMeta.name}</a> 
                                {item.musicMeta && (
                                    <a target="_blank" rel="noopener noreferrer" title={`♬ ${item.musicMeta.musicName} - ${item.musicMeta.musicAuthor}`} href={`https://www.tiktok.com/music/original-sound-`}>♬ {item.musicMeta.musicName} - {item.musicMeta.musicAuthor}</a>
                                )}
                            </section> 
                        </blockquote>
                     </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, color: 'text.disabled', borderTop: '1px solid #eee', pt: 1 }}>
                    <Typography variant="caption">By: @{item.authorMeta?.name || 'Unknown'}</Typography>
                    <Typography variant="caption">{new Date(item.createTime * 1000).toLocaleString()}</Typography>
                </Box>
                 <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', mt: 1, color: 'text.secondary' }}>
                    <Typography variant="caption">Likes: {item.diggCount || 0}</Typography>
                    <Typography variant="caption">Comments: {item.commentCount || 0}</Typography>
                    <Typography variant="caption">Shares: {item.shareCount || 0}</Typography>
                       <Typography variant="caption">Reach: {item.reach || item.reach || 0}</Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

